import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, Chip, Alert, Divider, Grid, TextField, Accordion,
  AccordionSummary, AccordionDetails, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, IconButton,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import HelpIcon from '@mui/icons-material/Help';


/* ─────────────────────────────────────────────────────────────────────────────
   BookingSettingsStep
   Étape "Réservation" du wizard CreateListingForm.
   Props :
     formData         – état formData du parent
     setFormData      – setter formData
     bookingSettings  – état bookingSettings du parent
     setBookingSettings – setter bookingSettings
   ──────────────────────────────────────────────────────────────────────────── */

export default function BookingSettingsStep({
  formData,
  setFormData,
  bookingSettings,
  setBookingSettings,
  externalFieldErrors = {},
  externalGlobalErrors = []
}) {

  const theme = useTheme();
  const { t } = useTranslation('booking');
  const [helpModalOpen, setHelpModalOpen] = useState(null); // 'q1' | 'q2' | 'q3' | 'q4' | 'q5' | 'q6' | null

  const WEEK_DAYS = [
    { day: 1, label: t('weekdays.1') },
    { day: 2, label: t('weekdays.2') },
    { day: 3, label: t('weekdays.3') },
    { day: 4, label: t('weekdays.4') },
    { day: 5, label: t('weekdays.5') },
    { day: 6, label: t('weekdays.6') },
    { day: 7, label: t('weekdays.7') },
  ];

  const colors = {
    q1: theme.palette.primary.main,
    q2: theme.palette.success.main,
    q3: theme.palette.warning.main,
    q4: theme.palette.info.main,
    q5: theme.palette.secondary.main,
    q6: theme.palette.success.dark || theme.palette.success.main,
  };

  const bookingMode = formData?.booking_mode;
  const stockQuantity = formData?.stock_quantity ?? 1;
  const openingHours = Array.isArray(bookingSettings?.opening_hours) ? bookingSettings.opening_hours : [];

  const isSlotBased = bookingMode === 'time_slot' || bookingMode === 'time_slot_capacity';
  const isShared    = bookingMode === 'time_slot_capacity';
  const isDateRange = bookingMode === 'date_range';

  const toIntOrNull = (value) => {
    const n = parseInt(value, 10);
    return Number.isNaN(n) ? null : n;
  };

  const minDuration = toIntOrNull(bookingSettings?.min_duration);
  const maxDuration = toIntOrNull(bookingSettings?.max_duration);
  const rawSlotDuration = toIntOrNull(bookingSettings?.slot_duration);
  const slotDuration = rawSlotDuration || 0;
  const bufferBefore = toIntOrNull(bookingSettings?.buffer_before) || 0;
  const bufferAfter = toIntOrNull(bookingSettings?.buffer_after) || 0;
  const slotStepMinutes = slotDuration + bufferBefore + bufferAfter;

  const openingHoursEffective = useMemo(() => {
    if (!isSlotBased) return [];
    if (openingHours.length === 0) {
      // Convention harmonisée avec le backend : vide = 24h/24, 7j/7
      return WEEK_DAYS.map(({ day }) => ({ day, from: '00:00', to: '23:59' }));
    }
    return openingHours;
  }, [isSlotBased, openingHours]);

  const toMinutes = (time) => {
    if (!time || typeof time !== 'string' || !time.includes(':')) return null;
    const [h, m] = time.split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    return h * 60 + m;
  };

  const openingIssues = useMemo(() => {
    return openingHours
      .map((h) => {
        const fromMin = toMinutes(h.from);
        const toMin = toMinutes(h.to);
        if (fromMin === null || toMin === null) {
          return t('bookingSettings.openingHours.invalidSchedule', { day: WEEK_DAYS.find(d => d.day === h.day)?.label || t('bookingSettings.openingHours.dayFallback', { day: h.day }) });
        }
        if (toMin <= fromMin) {
          return t('bookingSettings.openingHours.endAfterStart', { day: WEEK_DAYS.find(d => d.day === h.day)?.label || t('bookingSettings.openingHours.dayFallback', { day: h.day }) });
        }
        return null;
      })
      .filter(Boolean);
  }, [openingHours]);

  const maxWindowMinutes = useMemo(() => {
    const windows = openingHoursEffective
      .map((h) => {
        const fromMin = toMinutes(h.from);
        const toMin = toMinutes(h.to);
        if (fromMin === null || toMin === null || toMin <= fromMin) return 0;
        return toMin - fromMin;
      });
    return windows.length ? Math.max(...windows) : 0;
  }, [openingHoursEffective]);

  const feasibleConsecutiveSlots = useMemo(() => {
    if (!isSlotBased) return null;
    if (!slotDuration || slotDuration <= 0 || !slotStepMinutes || slotStepMinutes <= 0) return null;
    if (maxWindowMinutes < slotDuration) return 0;
    return Math.floor((maxWindowMinutes - slotDuration) / slotStepMinutes) + 1;
  }, [isSlotBased, slotDuration, slotStepMinutes, maxWindowMinutes]);

  const maxLessThanMin = minDuration && maxDuration && maxDuration < minDuration;
  const capacityDefault = toIntOrNull(formData?.capacity_default);

  // Auto-correction douce des incohérences bloquantes
  React.useEffect(() => {
    if (!isSlotBased) return;

    const patch = {};
    let changed = false;

    if (minDuration && maxDuration && maxDuration < minDuration) {
      patch.max_duration = String(minDuration);
      changed = true;
    }

    const maxForWindow = feasibleConsecutiveSlots;
    if (Number.isInteger(maxForWindow) && maxForWindow > 0) {
      if (minDuration && minDuration > maxForWindow) {
        patch.min_duration = String(maxForWindow);
        changed = true;
      }
      const currentMax = patch.max_duration ? parseInt(patch.max_duration, 10) : maxDuration;
      if (currentMax && currentMax > maxForWindow) {
        patch.max_duration = String(maxForWindow);
        changed = true;
      }
    }

    if (changed) {
      setBookingSettings((prev) => ({ ...(prev || {}), ...patch }));
    }
  }, [isSlotBased, minDuration, maxDuration, feasibleConsecutiveSlots, setBookingSettings]);

  // FIX: Nettoyage des champs orphelins lors du changement de mode
  React.useEffect(() => {
    if (isDateRange) {
      // En mode date_range, nettoyer les champs propres aux créneaux
      setBookingSettings(prev => {
        const p = prev || {};
        const needsClean =
          p.slot_duration || p.buffer_before || p.buffer_after ||
          (Array.isArray(p.opening_hours) && p.opening_hours.length > 0);
        if (!needsClean) return p;
        return {
          ...p,
          slot_duration: '',
          buffer_before: 0,
          buffer_after: 0,
          opening_hours: [],
        };
      });
    }
  }, [isDateRange, setBookingSettings]);

  // FIX: Nettoyage de capacity_default quand on quitte le mode partagé
  React.useEffect(() => {
    if (!isShared && formData?.capacity_default) {
      setFormData(prev => ({ ...(prev || {}), capacity_default: '' }));
    }
  }, [isShared]); // eslint-disable-line react-hooks/exhaustive-deps

  const coherenceAlerts = useMemo(() => {
    const alerts = [];

    // ── Mode non sélectionné ──
    if (!bookingMode) {
      alerts.push({ severity: 'warning', message: t('bookingSettings.alerts.selectMode') });
    }

    // ── Durées ──
    if (minDuration !== null && minDuration < 1) {
      alerts.push({ severity: 'error', message: t('bookingSettings.alerts.minDurationInvalid') });
    }
    if (maxDuration !== null && maxDuration < 1) {
      alerts.push({ severity: 'error', message: t('bookingSettings.alerts.maxDurationInvalid') });
    }
    if (maxLessThanMin) {
      alerts.push({ severity: 'error', message: t('bookingSettings.alerts.maxLessThanMin') });
    }

    // ── Mode partagé : capacity_default ──
    if (isShared) {
      if (capacityDefault === null || capacityDefault < 2) {
        alerts.push({ severity: 'error', message: t('bookingSettings.alerts.capacityMin2') });
      }
    }

    // ── Mode créneaux ──
    if (isSlotBased) {
      // slot_duration obligatoire et valide
      if (!slotDuration || slotDuration <= 0) {
        alerts.push({ severity: 'error', message: t('bookingSettings.alerts.slotDurationRequired') });
      } else if (slotDuration < 5) {
        alerts.push({ severity: 'error', message: t('bookingSettings.alerts.slotDurationMin5') });
      }
      if (slotDuration > 0 && slotDuration % 5 !== 0) {
        alerts.push({ severity: 'warning', message: t('bookingSettings.alerts.slotDurationMultiple5') });
      }

      // Tampons (buffers) cohérents
      if (bufferBefore < 0) {
        alerts.push({ severity: 'error', message: t('bookingSettings.alerts.bufferBeforeNegative') });
      }
      if (bufferAfter < 0) {
        alerts.push({ severity: 'error', message: t('bookingSettings.alerts.bufferAfterNegative') });
      }
      if (slotDuration > 0 && (bufferBefore + bufferAfter) >= slotDuration * 3) {
        alerts.push({ severity: 'warning', message: t('bookingSettings.alerts.buffersHighWarning') });
      }

      // Cohérence unité / mode créneau — supprimée car désormais verrouillée dans l'UI
      // (l'unité "Jour" est physiquement désactivée en mode créneaux)

      // Horaires d'ouverture
      if (openingIssues.length > 0) {
        openingIssues.forEach((msg) => alerts.push({ severity: 'error', message: msg }));
      }

      // Faisabilité des créneaux vs plages horaires
      if (Number.isInteger(feasibleConsecutiveSlots)) {
        if (feasibleConsecutiveSlots === 0) {
          alerts.push({ severity: 'error', message: t('bookingSettings.alerts.noSlotGenerated') });
        } else {
          if (minDuration && minDuration > feasibleConsecutiveSlots) {
            alerts.push({
              severity: 'error',
              message: t('bookingSettings.alerts.minExceedsMaxWindow', { count: feasibleConsecutiveSlots }),
            });
          }
          if (maxDuration && maxDuration > feasibleConsecutiveSlots) {
            alerts.push({
              severity: 'warning',
              message: t('bookingSettings.alerts.maxAutoReduced', { count: feasibleConsecutiveSlots }),
            });
          }
        }
      }

      // Cohérence unit=hour vs slot_duration
      if (bookingSettings?.unit === 'hour' && slotDuration > 0 && slotDuration !== 60 && 60 % slotDuration !== 0 && slotDuration % 60 !== 0) {
        alerts.push({
          severity: 'warning',
          message: t('bookingSettings.alerts.hourUnitSlotMismatch', { duration: slotDuration }),
        });
      }
    }

    // ── Mode date_range : cohérence unité ──
    if (isDateRange && bookingSettings?.unit && bookingSettings.unit !== 'day') {
      alerts.push({ severity: 'error', message: t('bookingSettings.alerts.dateRangeUnitMustBeDay') });
    }

    return alerts;
  }, [
    bookingMode,
    minDuration,
    maxDuration,
    maxLessThanMin,
    isSlotBased,
    isShared,
    isDateRange,
    capacityDefault,
    slotDuration,
    bufferBefore,
    bufferAfter,
    bookingSettings?.unit,
    openingIssues,
    feasibleConsecutiveSlots,
  ]);

  // ✅ Validation : forcer l'unité de facturation en fonction du mode de réservation
  const forcedUnit = isDateRange ? 'day' : null; // Si par journées → jour obligatoire
  
  // Synchroniser automatiquement l'unité si le mode change
  React.useEffect(() => {
    if (forcedUnit && bookingSettings?.unit !== forcedUnit) {
      setBookingSettings(p => ({ ...(p || {}), unit: forcedUnit }));
    }
  }, [forcedUnit, bookingSettings?.unit, setBookingSettings]);

  // Verrouillage UX : en mode créneaux, l'unité "Jour" est interdite.
  // Auto-corrige les anciens listings ou états corrompus.
  React.useEffect(() => {
    if (!isSlotBased) return;
    if (bookingSettings?.unit !== 'day') return;
    setBookingSettings(p => ({ ...(p || {}), unit: 'slot' }));
  }, [isSlotBased, bookingSettings?.unit, setBookingSettings]);

  // Verrouillage UX : en mode partagé, per_seat et per_unit_per_seat sont unifiés selon le stock.
  // stock=1 → per_seat (équivalent, plus simple)
  // stock>1 → per_unit_per_seat (per_seat seul est dangereux : les unités ne sont pas bloquées)
  React.useEffect(() => {
    if (!isShared) return;
    setBookingSettings(prev => {
      const p = prev || {};
      const pm = p.pricing_mode;
      if (pm === 'flat') return prev;
      if (stockQuantity > 1 && pm === 'per_seat') {
        return { ...p, pricing_mode: 'per_unit_per_seat' };
      }
      if (stockQuantity === 1 && pm === 'per_unit_per_seat') {
        return { ...p, pricing_mode: 'per_seat' };
      }
      if (!pm || pm === 'per_unit') {
        return { ...p, pricing_mode: stockQuantity > 1 ? 'per_unit_per_seat' : 'per_seat' };
      }
      return prev;
    });
  }, [isShared, stockQuantity, setBookingSettings]);

  /* ── styles communs ── */
  const cardSx = (active, color = colors.q1, disabled = false) => ({
    flex: 1, minWidth: 140,
    border: `2px solid ${active ? color : theme.palette.divider}`,
    borderRadius: 3, p: 2,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.45 : 1,
    backgroundColor: active ? alpha(color, 0.08) : theme.palette.background.default,
    transition: 'all 0.2s',
    boxShadow: active ? theme.shadows[4] : 'none',
    pointerEvents: disabled ? 'none' : 'auto',
    '&:hover': disabled ? {} : { borderColor: color, backgroundColor: alpha(color, 0.06) },
  });

  const subSx = (color) => ({
    mb: 2,
    border: `2px solid ${alpha(color, 0.30)}`,
    borderRadius: '12px !important',
    overflow: 'hidden',
    backgroundColor: alpha(color, 0.04),
    '&:before': { display: 'none' },
    boxShadow: 'none',
  });

  const sumSx = (color) => ({
    background: `linear-gradient(135deg, ${alpha(color, 0.18)}, ${alpha(color, 0.08)})`,
    borderBottom: `1px solid ${alpha(color, 0.20)}`,
    minHeight: '52px !important',
    '&.Mui-expanded': { minHeight: '52px !important' },
  });

  const QNum = ({ n }) => (
    <Box
      sx={{
        width: 26,
        height: 26,
        borderRadius: '50%',
        background: `linear-gradient(135deg,${colors.q1},${colors.q5})`,
        color: theme.palette.common.white,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '0.8rem',
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {n}
    </Box>
  );

  // Contenu des modals d'aide
  const buildExamples = (qKey, count) =>
    Array.from({ length: count }, (_, i) => ({
      title: t(`bookingSettings.modal.${qKey}.ex${i + 1}.title`),
      desc:  t(`bookingSettings.modal.${qKey}.ex${i + 1}.desc`),
    }));

  const modalContent = {
    q1: { title: t('bookingSettings.modal.q1.title'), examples: buildExamples('q1', 3) },
    q2: { title: t('bookingSettings.modal.q2.title'), examples: buildExamples('q2', 3) },
    q3: { title: t('bookingSettings.modal.q3.title'), examples: buildExamples('q3', 3) },
    q4: { title: t('bookingSettings.modal.q4.title'), examples: buildExamples('q4', 5) },
    q5: { title: t('bookingSettings.modal.q5.title'), examples: buildExamples('q5', 5) },
    q6: { title: t('bookingSettings.modal.q6.title'), examples: buildExamples('q6', 5) },
  };

  return (
    <React.Fragment>
      {/* ══════════════════════════════════════════════════════════════
          Q1 — Comment réserve-t-on ?
      ══════════════════════════════════════════════════════════════ */}
      <Accordion defaultExpanded sx={subSx(colors.q1)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={sumSx(colors.q1)}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: colors.q1 }} />
            <Typography fontWeight={700} sx={{ color: colors.q1 }}>{t('bookingSettings.q1.sectionTitle')}</Typography>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); setHelpModalOpen('q1'); }}
              sx={{ ml: 0.5, color: colors.q1, '&:hover': { background: alpha(colors.q1, 0.15) } }}
            >
              <HelpIcon sx={{ fontSize: 18 }} />
            </IconButton>
            {formData.booking_mode && (
              <Chip size="small"
                label={
                  formData.booking_mode === 'date_range'
                    ? (bookingSettings?.date_range_mode === 'nights' ? t('bookingSettings.q1.chip.nights') : t('bookingSettings.q1.chip.days'))
                    : formData.booking_mode === 'time_slot'  ? t('bookingSettings.q1.chip.timeSlot') : t('bookingSettings.q1.chip.sharedSlot')
                }
                sx={{
                  ml: 1,
                  backgroundColor: colors.q1,
                  color: theme.palette.getContrastText(colors.q1),
                  fontWeight: 600,
                  height: 22,
                  fontSize: '0.7rem',
                }}
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 2, pb: 3, px: 3 }}>
          <Box display="flex" alignItems="center" gap={1.5} mb={2}>
            <QNum n="1" />
            <Typography variant="subtitle1" fontWeight={600}>
              {t('bookingSettings.q1.bodyTitle')}
            </Typography>
          </Box>
          <Box display="flex" gap={2} flexWrap="wrap">
            {/* Par journées */}
            <Box sx={cardSx(bookingMode === 'date_range')}
              onClick={() => {
                // Defaults SYNCHRONES - appliqués dans le même batch React
                setFormData(p => ({
                  ...(p || {}),
                  booking_mode: 'date_range',
                  capacity_default: '', // pas de places en date_range
                  pricePerSlot: '',     // pas de prix créneau
                }));
                setBookingSettings(p => ({
                  ...(p || {}),
                  unit: 'day',
                  capacity_mode: 'exclusive',
                  pricing_mode: 'per_unit',
                  // Nettoyer les champs propres aux créneaux
                  slot_duration: '',
                  buffer_before: 0,
                  buffer_after: 0,
                  opening_hours: [],
                }));
              }}>
              <Typography fontSize="2rem" textAlign="center">📅</Typography>
              <Typography fontWeight={700} textAlign="center" mb={0.5}>{t('bookingSettings.q1.card.dateRange.label')}</Typography>
              <Typography variant="caption" color="text.secondary" display="block" textAlign="center">
                {t('bookingSettings.q1.card.dateRange.desc')}
              </Typography>
              {bookingMode === 'date_range' &&
                <Box textAlign="center" mt={1}><CheckCircleIcon sx={{ color: colors.q1, fontSize: 18 }} /></Box>}
            </Box>

            {/* Créneaux horaires */}
            <Box sx={cardSx(bookingMode === 'time_slot')}
              onClick={() => {
                // Defaults SYNCHRONES - appliqués dans le même batch React
                setFormData(p => ({
                  ...(p || {}),
                  booking_mode: 'time_slot',
                  capacity_default: '', // créneaux exclusifs = pas de places partagées
                }));
                setBookingSettings(p => {
                  // Retirer les champs propres au mode date_range
                  const { date_range_mode: _dm, ...rest } = p || {};
                  return {
                    ...rest,
                    unit: 'slot',
                    capacity_mode: 'exclusive',
                    pricing_mode: 'per_unit',
                    // Initialiser slot_duration à 60 min si pas encore défini
                    slot_duration: p?.slot_duration || '60',
                  };
                });
              }}>
              <Typography fontSize="2rem" textAlign="center">🕐</Typography>
              <Typography fontWeight={700} textAlign="center" mb={0.5}>{t('bookingSettings.q1.card.timeSlot.label')}</Typography>
              <Typography variant="caption" color="text.secondary" display="block" textAlign="center">
                {t('bookingSettings.q1.card.timeSlot.desc')}
              </Typography>
              {bookingMode === 'time_slot' &&
                <Box textAlign="center" mt={1}><CheckCircleIcon sx={{ color: colors.q1, fontSize: 18 }} /></Box>}
            </Box>

            {/* Créneaux + places */}
            <Box sx={cardSx(bookingMode === 'time_slot_capacity')}
              onClick={() => {
                // Defaults SYNCHRONES - appliqués dans le même batch React
                setFormData(p => ({
                  ...(p || {}),
                  booking_mode: 'time_slot_capacity',
                  // Capacité ≥ 2 obligatoire : auto-initialiser si vide/inférieur
                  capacity_default: (() => {
                    const cur = parseInt((p || {}).capacity_default, 10);
                    return (!Number.isNaN(cur) && cur >= 2) ? String(cur) : '2';
                  })(),
                }));
                setBookingSettings(p => {
                  // Retirer les champs propres au mode date_range
                  const { date_range_mode: _dm, ...rest } = p || {};
                  return {
                    ...rest,
                    unit: 'slot',
                    capacity_mode: 'shared',
                    // Initialiser slot_duration à 60 min si pas encore défini
                    slot_duration: p?.slot_duration || '60',
                    // Défaut au passage en mode partagé selon le stock :
                    //   flat préservé, per_seat↔per_unit_per_seat normalisés selon stock
                    pricing_mode: p?.pricing_mode === 'flat'
                      ? 'flat'
                      : stockQuantity > 1 ? 'per_unit_per_seat' : 'per_seat',
                  };
                });
              }}>
              <Typography fontSize="2rem" textAlign="center">👥</Typography>
              <Typography fontWeight={700} textAlign="center" mb={0.5}>{t('bookingSettings.q1.card.sharedSlot.label')}</Typography>
              <Typography variant="caption" color="text.secondary" display="block" textAlign="center">
                {t('bookingSettings.q1.card.sharedSlot.desc')}
              </Typography>
              {bookingMode === 'time_slot_capacity' &&
                <Box textAlign="center" mt={1}><CheckCircleIcon sx={{ color: colors.q1, fontSize: 18 }} /></Box>}
            </Box>
          </Box>
          {isDateRange && (
            <Alert severity="warning" sx={{ mt: 2.5, fontSize: '0.85rem' }}>
              {t('bookingSettings.q1.alertDateRangeImpact')}
            </Alert>
          )}



          {/* Sous-question Q1b : Nuitées vs Journées */}
          {isDateRange && (
            <Box sx={{ mt: 2.5 }}>
              <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
                <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                  {t('bookingSettings.q1.subQ.durationTitle')}
                </Typography>
              </Box>
              <Box display="flex" gap={2} flexWrap="wrap">
                {/* Journées */}
                <Box
                  sx={cardSx(bookingSettings?.date_range_mode !== 'nights', colors.q1)}
                  onClick={() => setBookingSettings(p => ({ ...(p || {}), date_range_mode: 'days' }))}
                >
                  <Typography fontSize="1.6rem" textAlign="center">🛠️</Typography>
                  <Typography fontWeight={700} textAlign="center" fontSize="0.9rem" mb={0.5}>{t('bookingSettings.q1.subQ.days.label')}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block" textAlign="center">
                    {t('bookingSettings.q1.subQ.days.desc')}
                  </Typography>
                  {bookingSettings?.date_range_mode !== 'nights' &&
                    <Box textAlign="center" mt={0.5}><CheckCircleIcon sx={{ color: colors.q1, fontSize: 16 }} /></Box>}
                </Box>

                {/* Nuitées */}
                <Box
                  sx={cardSx(bookingSettings?.date_range_mode === 'nights', colors.q1)}
                  onClick={() => setBookingSettings(p => ({ ...(p || {}), date_range_mode: 'nights' }))}
                >
                  <Typography fontSize="1.6rem" textAlign="center">🏨</Typography>
                  <Typography fontWeight={700} textAlign="center" fontSize="0.9rem" mb={0.5}>{t('bookingSettings.q1.subQ.nights.label')}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block" textAlign="center">
                    {t('bookingSettings.q1.subQ.nights.desc')}
                  </Typography>
                  {bookingSettings?.date_range_mode === 'nights' &&
                    <Box textAlign="center" mt={0.5}><CheckCircleIcon sx={{ color: colors.q1, fontSize: 16 }} /></Box>}
                </Box>
              </Box>
              <Alert severity="info" sx={{ mt: 1.5, fontSize: '0.82rem' }}>
                {bookingSettings?.date_range_mode === 'nights'
                  ? t('bookingSettings.q1.subQ.alertNights')
                  : t('bookingSettings.q1.subQ.alertDays')}
              </Alert>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* ══════════════════════════════════════════════════════════════
          Q2 — Combien d'exemplaires simultanés ?
      ══════════════════════════════════════════════════════════════ */}
      <Accordion defaultExpanded sx={subSx(colors.q2)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={sumSx(colors.q2)}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: colors.q2 }} />
            <Typography fontWeight={700} sx={{ color: colors.q2 }}>{t('bookingSettings.q2.sectionTitle')}</Typography>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); setHelpModalOpen('q2'); }}
              sx={{ ml: 0.5, color: colors.q2, '&:hover': { background: alpha(colors.q2, 0.15) } }}
            >
              <HelpIcon sx={{ fontSize: 18 }} />
            </IconButton>
            {stockQuantity > 1 && (
              <Chip size="small" label={t('bookingSettings.q2.chipUnits', { count: stockQuantity })}
                sx={{
                  ml: 1,
                  backgroundColor: colors.q2,
                  color: theme.palette.getContrastText(colors.q2),
                  fontWeight: 600,
                  height: 22,
                  fontSize: '0.7rem',
                }} />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 2, pb: 3, px: 3 }}>
          <Box display="flex" alignItems="center" gap={1.5} mb={2}>
            <QNum n="2" />
            <Typography variant="subtitle1" fontWeight={600}>
              {t('bookingSettings.q2.bodyTitle')}
            </Typography>
          </Box>
          <Box display="flex" gap={1.5} alignItems="flex-start" flexWrap="wrap">
            {[1, 2, 3, 5, 10].map(n => (
              <Box key={n}
                onClick={() => setFormData(p => ({ ...(p || {}), stock_quantity: n }))}
                sx={{
                  width: 52, height: 52, borderRadius: 2, cursor: 'pointer',
                  fontWeight: 700, fontSize: '1.1rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.15s',
                  border: `2px solid ${stockQuantity === n ? colors.q2 : theme.palette.divider}`,
                  backgroundColor: stockQuantity === n ? alpha(colors.q2, 0.12) : theme.palette.background.default,
                  color: stockQuantity === n ? colors.q2 : theme.palette.text.secondary,
                  '&:hover': { borderColor: colors.q2, backgroundColor: alpha(colors.q2, 0.12) },
                }}>{n}</Box>
            ))}
            <TextField type="number" size="small" label={t('bookingSettings.common.other')}
              value={stockQuantity}
              onChange={e => setFormData(p => ({ ...(p || {}), stock_quantity: Math.max(1, parseInt(e.target.value) || 1) }))}
              inputProps={{ min: 1, step: 1 }} sx={{ width: 110 }} />
          </Box>
          <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
            {stockQuantity === 1
              ? t('bookingSettings.q2.captionSingle')
              : t('bookingSettings.q2.captionMultiple', { count: stockQuantity })}
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* ══════════════════════════════════════════════════════════════
          Q3 — Places par créneau (uniquement si mode partagé)
      ══════════════════════════════════════════════════════════════ */}
      {isShared && (
        <Accordion defaultExpanded sx={subSx(colors.q3)}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={sumSx(colors.q3)}>
            <Box display="flex" alignItems="center" gap={1}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: colors.q3 }} />
              <Typography fontWeight={700} sx={{ color: colors.q3 }}>{t('bookingSettings.q3.sectionTitle')}</Typography>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); setHelpModalOpen('q3'); }}
                sx={{ ml: 0.5, color: colors.q3, '&:hover': { background: alpha(colors.q3, 0.15) } }}
              >
                <HelpIcon sx={{ fontSize: 18 }} />
              </IconButton>
              {formData.capacity_default && (
                <Chip size="small" label={t('bookingSettings.q3.chipPlaces', { count: formData.capacity_default })}
                  sx={{
                    ml: 1,
                    backgroundColor: colors.q3,
                    color: theme.palette.getContrastText(colors.q3),
                    fontWeight: 600,
                    height: 22,
                    fontSize: '0.7rem',
                  }} />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 2, pb: 3, px: 3 }}>
            <Box display="flex" alignItems="center" gap={1.5} mb={2}>
              <QNum n="3" />
              <Typography variant="subtitle1" fontWeight={600}>
                {t('bookingSettings.q3.bodyTitle')}
              </Typography>
            </Box>
            <Box display="flex" gap={1.5} alignItems="flex-start" flexWrap="wrap">
              {[2, 5, 10, 20, 50].map(n => (
                <Box key={n}
                  onClick={() => setFormData(p => ({ ...(p || {}), capacity_default: String(n) }))}
                  sx={{
                    width: 52, height: 52, borderRadius: 2, cursor: 'pointer',
                    fontWeight: 700, fontSize: '1rem',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.15s',
                    border: `2px solid ${String(formData.capacity_default) === String(n) ? colors.q3 : theme.palette.divider}`,
                    backgroundColor: String(formData.capacity_default) === String(n) ? alpha(colors.q3, 0.12) : theme.palette.background.default,
                    color: String(formData.capacity_default) === String(n) ? colors.q3 : theme.palette.text.secondary,
                    '&:hover': { borderColor: colors.q3, backgroundColor: alpha(colors.q3, 0.12) },
                  }}>{n}</Box>
              ))}
              <TextField type="number" size="small" label={t('bookingSettings.common.other')}
                value={formData?.capacity_default}
                onChange={e => setFormData(p => ({ ...(p || {}), capacity_default: e.target.value }))}
                inputProps={{ min: 2, step: 1 }} sx={{ width: 110 }} />
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
              {t('bookingSettings.q3.captionClose')}
            </Typography>
          </AccordionDetails>
        </Accordion>
      )}

      {/* ══════════════════════════════════════════════════════════════
          Q3b — Mode de tarification (créneaux uniquement)
      ══════════════════════════════════════════════════════════════ */}
      {isSlotBased && (
        <Accordion defaultExpanded sx={subSx(colors.q4)}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={sumSx(colors.q4)}>
            <Box display="flex" alignItems="center" gap={1}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: colors.q4 }} />
              <Typography fontWeight={700} sx={{ color: colors.q4 }}>{t('bookingSettings.q3b.sectionTitle')}</Typography>
              {bookingSettings?.pricing_mode && (
                <Chip size="small"
                  label={{
                    per_unit:          t('bookingSettings.q3b.pricingMode.perUnit'),
                    per_seat:          t('bookingSettings.q3b.pricingMode.perSeat'),
                    per_unit_per_seat: t('bookingSettings.q3b.pricingMode.perUnitPerSeat'),
                    flat:              t('bookingSettings.q3b.pricingMode.flat'),
                  }[bookingSettings.pricing_mode] || bookingSettings.pricing_mode}
                  sx={{
                    ml: 1,
                    backgroundColor: colors.q4,
                    color: theme.palette.getContrastText(colors.q4),
                    fontWeight: 600, height: 22, fontSize: '0.7rem',
                  }} />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 2, pb: 3, px: 3 }}>
            <Box display="flex" alignItems="center" gap={1.5} mb={2}>
              <QNum n={isShared ? '4' : '3'} />
              <Typography variant="subtitle1" fontWeight={600}>
                {t('bookingSettings.q3b.bodyTitle')}
              </Typography>
            </Box>

            <Box display="flex" gap={2} flexWrap="wrap">
              {/* per_unit — autorisé uniquement en créneaux exclusifs, grisé en partagé */}
              <Box
                sx={cardSx(bookingSettings?.pricing_mode === 'per_unit', colors.q4, isShared)}
                onClick={() => {
                  if (isShared) return;
                  setBookingSettings(p => ({ ...(p || {}), pricing_mode: 'per_unit' }));
                }}>
                <Typography fontSize="1.6rem" textAlign="center">📦</Typography>
                <Typography fontWeight={700} textAlign="center" fontSize="0.9rem" mb={0.5}>{t('bookingSettings.q3b.pricingMode.perUnit')}</Typography>
                <Typography variant="caption" color="text.secondary" display="block" textAlign="center">
                  {t('bookingSettings.q3b.perUnit.desc')}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={0.5} fontStyle="italic">
                  {isShared
                    ? t('bookingSettings.q3b.perUnit.sharedUnavailable')
                    : t('bookingSettings.q3b.perUnit.exclDesc')}
                </Typography>
                {bookingSettings?.pricing_mode === 'per_unit' &&
                  <Box textAlign="center" mt={1}><CheckCircleIcon sx={{ color: colors.q4, fontSize: 18 }} /></Box>}
              </Box>

              {/* per_seat — stock=1 uniquement : 1 exemplaire, facturation à la place */}
              {isShared && stockQuantity === 1 && (
                <Box
                  sx={cardSx(bookingSettings?.pricing_mode === 'per_seat', colors.q4)}
                  onClick={() => {
                    setBookingSettings(p => ({ ...(p || {}), pricing_mode: 'per_seat' }));
                  }}>
                  <Typography fontSize="1.6rem" textAlign="center">🧑‍🤝‍🧑</Typography>
                  <Typography fontWeight={700} textAlign="center" fontSize="0.9rem" mb={0.5}>{t('bookingSettings.q3b.pricingMode.perSeat')}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block" textAlign="center">
                    {t('bookingSettings.q3b.perSeat.desc')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={0.5} fontStyle="italic">
                    {t('bookingSettings.q3b.perSeat.ex')}
                  </Typography>
                  {bookingSettings?.pricing_mode === 'per_seat' &&
                    <Box textAlign="center" mt={1}><CheckCircleIcon sx={{ color: colors.q4, fontSize: 18 }} /></Box>}
                </Box>
              )}

              {/* per_unit_per_seat — stock>1 uniquement : plusieurs exemplaires, facturation unité × place */}
              {isShared && stockQuantity > 1 && (
                <Box
                  sx={cardSx(bookingSettings?.pricing_mode === 'per_unit_per_seat', colors.q4)}
                  onClick={() => {
                    setBookingSettings(p => ({ ...(p || {}), pricing_mode: 'per_unit_per_seat' }));
                  }}>
                  <Typography fontSize="1.6rem" textAlign="center">🪑✕📦</Typography>
                  <Typography fontWeight={700} textAlign="center" fontSize="0.9rem" mb={0.5}>{t('bookingSettings.q3b.pricingMode.perUnitPerSeat')}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block" textAlign="center">
                    {t('bookingSettings.q3b.perUnitPerSeat.desc', { count: stockQuantity })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={0.5} fontStyle="italic">
                    {t('bookingSettings.q3b.perUnitPerSeat.ex', { count: stockQuantity })}
                  </Typography>
                  {bookingSettings?.pricing_mode === 'per_unit_per_seat' &&
                    <Box textAlign="center" mt={1}><CheckCircleIcon sx={{ color: colors.q4, fontSize: 18 }} /></Box>}
                </Box>
              )}

              {/* flat — toujours disponible */}
              <Box sx={cardSx(bookingSettings?.pricing_mode === 'flat', colors.q4)}
                onClick={() => setBookingSettings(p => ({ ...(p || {}), pricing_mode: 'flat' }))}>
                <Typography fontSize="1.6rem" textAlign="center">🏷️</Typography>
                <Typography fontWeight={700} textAlign="center" fontSize="0.9rem" mb={0.5}>{t('bookingSettings.q3b.pricingMode.flat')}</Typography>
                <Typography variant="caption" color="text.secondary" display="block" textAlign="center">
                  {t('bookingSettings.q3b.flat.desc')}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={0.5} fontStyle="italic">
                  {t('bookingSettings.q3b.flat.ex')}
                </Typography>
                {bookingSettings?.pricing_mode === 'flat' &&
                  <Box textAlign="center" mt={1}><CheckCircleIcon sx={{ color: colors.q4, fontSize: 18 }} /></Box>}
              </Box>
            </Box>

            {/* Résumé de la formule sélectionnée */}
            {bookingSettings?.pricing_mode && (
              <Alert severity="success" sx={{ mt: 2.5, fontSize: '0.85rem' }}>
                <strong>{t('bookingSettings.q3b.formulaTitle')}</strong>{' '}
                {{
                  per_unit:          t('bookingSettings.q3b.formulas.perUnit', { count: stockQuantity }),
                  per_seat:          t('bookingSettings.q3b.formulas.perSeat'),
                  per_unit_per_seat: t('bookingSettings.q3b.formulas.perUnitPerSeat', { count: stockQuantity }),
                  flat:              t('bookingSettings.q3b.formulas.flat'),
                }[bookingSettings.pricing_mode]}
              </Alert>
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {/* ══════════════════════════════════════════════════════════════
          Q4 — Durée & unité de facturation
      ══════════════════════════════════════════════════════════════ */}
      <Accordion defaultExpanded sx={subSx(colors.q4)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={sumSx(colors.q4)}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: colors.q4 }} />
            <Typography fontWeight={700} sx={{ color: colors.q4 }}>{t('bookingSettings.q4.sectionTitle')}</Typography>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); setHelpModalOpen('q4'); }}
              sx={{ ml: 0.5, color: colors.q4, '&:hover': { background: alpha(colors.q4, 0.15) } }}
            >
              <HelpIcon sx={{ fontSize: 18 }} />
            </IconButton>
            {(bookingSettings.min_duration || bookingSettings.max_duration) && (
              <Chip size="small"
                label={[
                  bookingSettings.min_duration && `min ${bookingSettings.min_duration}`,
                  bookingSettings.max_duration && `max ${bookingSettings.max_duration}`,
                ].filter(Boolean).join(' · ')}
                sx={{
                  ml: 1,
                  backgroundColor: colors.q4,
                  color: theme.palette.getContrastText(colors.q4),
                  fontWeight: 600,
                  height: 22,
                  fontSize: '0.7rem',
                }} />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 2, pb: 3, px: 3 }}>
          <Box display="flex" alignItems="center" gap={1.5} mb={2}>
            <QNum n={isShared ? '5' : '4'} />
            <Typography variant="subtitle1" fontWeight={600}>
              {t('bookingSettings.q4.bodyTitle')}
            </Typography>
          </Box>
          <Alert severity="info" icon="💡" sx={{ mb: 2.5, fontSize: '0.85rem' }}>
            <strong>{t('bookingSettings.q4.alertGuide.title')}</strong>
            {' '}{t('bookingSettings.q4.alertGuide.hint')}
            <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2 }}>
              <li>{t('bookingSettings.q4.alertGuide.noDesc')}</li>
              <li>{t('bookingSettings.q4.alertGuide.yesDesc')}</li>
            </Box>
          </Alert>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" fontWeight={600} mb={1} color="text.secondary">
                {t('bookingSettings.q4.unitTitle')}
              </Typography>
              {isDateRange && (
                <Alert severity="info" sx={{ mb: 1.5, fontSize: '0.85rem' }}>
                  {t('bookingSettings.q4.alertForcedDay')}
                </Alert>
              )}
              {isSlotBased && (
                <Alert severity="info" sx={{ mb: 1.5, fontSize: '0.85rem' }}>
                  {t('bookingSettings.q4.alertDayDisabled')}
                </Alert>
              )}
              <Box display="flex" gap={1}>
                {[
                  { v: 'day',  icon: '📅', l: t('bookingSettings.common.unitButtons.day')    },
                  { v: 'hour', icon: '🕐', l: t('bookingSettings.common.unitButtons.hour')   },
                  { v: 'slot', icon: '⏱️', l: t('bookingSettings.common.unitButtons.slot') },
                ].map(({ v, icon, l }) => {
                  const isDisabled = (isDateRange && v !== 'day') || (isSlotBased && v === 'day');
                  return (
                    <Box key={v}
                      onClick={() => !isDisabled && setBookingSettings(p => ({ ...(p || {}), unit: v }))}
                      sx={{
                        flex: 1, borderRadius: 2, p: 1, textAlign: 'center',
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        transition: 'all 0.15s',
                        border: `2px solid ${bookingSettings.unit === v ? colors.q4 : isDisabled ? theme.palette.action.disabled : theme.palette.divider}`,
                        backgroundColor: bookingSettings.unit === v ? alpha(colors.q4, 0.12) : isDisabled ? alpha(theme.palette.action.disabled, 0.05) : theme.palette.background.default,
                        opacity: isDisabled ? 0.5 : 1,
                        '&:hover': isDisabled ? {} : { borderColor: colors.q4, backgroundColor: alpha(colors.q4, 0.12) },
                      }}>
                      <Typography fontSize="1.1rem">{icon}</Typography>
                      <Typography variant="caption" fontWeight={bookingSettings.unit === v ? 700 : 400}>{l}</Typography>
                    </Box>
                  );
                })}
              </Box>
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField fullWidth type="number" size="small"
                label={t('bookingSettings.q4.minLabel', { unit: bookingSettings.unit === 'day' ? t('bookingSettings.common.unitPlural.day') : bookingSettings.unit === 'hour' ? t('bookingSettings.common.unitPlural.hour') : t('bookingSettings.common.unitPlural.slot') })}
                value={bookingSettings.min_duration}
                onChange={e => setBookingSettings(p => ({ ...(p || {}), min_duration: e.target.value }))}
                placeholder="ex: 1"
                error={(minDuration !== null && minDuration < 1) || !!externalFieldErrors.min_duration}
                helperText={
                  externalFieldErrors.min_duration ||
                  (minDuration !== null && minDuration < 1 ? t('bookingSettings.q4.minHelperError') : t('bookingSettings.q4.minHelperEmpty'))
                }
                inputProps={{ min: 1 }} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField fullWidth type="number" size="small"
                label={t('bookingSettings.q4.maxLabel', { unit: bookingSettings.unit === 'day' ? t('bookingSettings.common.unitPlural.day') : bookingSettings.unit === 'hour' ? t('bookingSettings.common.unitPlural.hour') : t('bookingSettings.common.unitPlural.session') })}
                value={bookingSettings.max_duration}
                onChange={e => setBookingSettings(p => ({ ...(p || {}), max_duration: e.target.value }))}
                placeholder={t('bookingSettings.q4.maxPlaceholder')}
                error={(maxDuration !== null && maxDuration < 1) || !!maxLessThanMin || !!externalFieldErrors.max_duration}
                helperText={
                  externalFieldErrors.max_duration
                    ? externalFieldErrors.max_duration
                    : maxLessThanMin
                    ? t('bookingSettings.q4.maxHelperMaxMin')
                    : (maxDuration !== null && maxDuration < 1)
                    ? t('bookingSettings.q4.maxHelperMin1')
                    : t('bookingSettings.q4.maxHelperEmpty')
                }
                inputProps={{ min: 1 }} />
            </Grid>
          </Grid>
          <Typography variant="caption" color="text.secondary" display="block" mt={1}>
            {t('bookingSettings.q4.captionNoLimit')}
          </Typography>
          
          {/* Alerte : impact sur les champs de prix */}
          <Alert severity="success" sx={{ mt: 2.5, fontSize: '0.9rem' }}>
            {t('bookingSettings.q4.alertUnitImpact', {
              unit: bookingSettings?.unit === 'day' ? t('bookingSettings.common.unitButtons.day') :
                    bookingSettings?.unit === 'hour' ? t('bookingSettings.common.unitButtons.hour') :
                    bookingSettings?.unit === 'slot' ? t('bookingSettings.common.unitButtons.slot') :
                    t('bookingSettings.q4.unitNotDefined')
            })}
          </Alert>
        </AccordionDetails>
      </Accordion>

      {/* ══════════════════════════════════════════════════════════════
          Q5 — Durée d'un créneau + tampons (si slot-based)
      ══════════════════════════════════════════════════════════════ */}
      {isSlotBased && (
        <Accordion defaultExpanded sx={subSx(colors.q5)}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={sumSx(colors.q5)}>
            <Box display="flex" alignItems="center" gap={1}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: colors.q5 }} />
              <Typography fontWeight={700} sx={{ color: colors.q5 }}>{t('bookingSettings.q5.sectionTitle')}</Typography>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); setHelpModalOpen('q5'); }}
                sx={{ ml: 0.5, color: colors.q5, '&:hover': { background: alpha(colors.q5, 0.15) } }}
              >
                <HelpIcon sx={{ fontSize: 18 }} />
              </IconButton>
              {bookingSettings.slot_duration && (
                <Chip size="small" label={`${bookingSettings.slot_duration} min`}
                  sx={{
                    ml: 1,
                    backgroundColor: colors.q5,
                    color: theme.palette.getContrastText(colors.q5),
                    fontWeight: 600,
                    height: 22,
                    fontSize: '0.7rem',
                  }} />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 2, pb: 3, px: 3 }}>
            <Box display="flex" alignItems="center" gap={1.5} mb={2}>
              <QNum n={isShared ? '6' : '5'} />
              <Typography variant="subtitle1" fontWeight={600}>
                {t('bookingSettings.q5.bodyTitle')}
              </Typography>
            </Box>
            <Box display="flex" gap={1.5} flexWrap="wrap" mb={3}>
              {[15, 30, 45, 60, 90, 120].map(min => (
                <Box key={min}
                  onClick={() => setBookingSettings(p => ({ ...(p || {}), slot_duration: String(min) }))}
                  sx={{
                    minWidth: 58, px: 1.5, py: 1, borderRadius: 2, cursor: 'pointer',
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    transition: 'all 0.15s',
                    border: `2px solid ${String(bookingSettings.slot_duration) === String(min) ? colors.q5 : theme.palette.divider}`,
                    backgroundColor: String(bookingSettings.slot_duration) === String(min) ? alpha(colors.q5, 0.12) : theme.palette.background.default,
                    '&:hover': { borderColor: colors.q5, backgroundColor: alpha(colors.q5, 0.12) },
                  }}>
                  <Typography fontWeight={700}
                    sx={{ color: String(bookingSettings.slot_duration) === String(min) ? colors.q5 : theme.palette.text.primary }}>
                    {min >= 60 ? `${min / 60}h` : `${min}min`}
                  </Typography>
                </Box>
              ))}
              <TextField type="number" size="small" label={t('bookingSettings.q5.otherMin')}
                value={bookingSettings.slot_duration}
                onChange={e => setBookingSettings(p => ({ ...(p || {}), slot_duration: e.target.value }))}
                error={!!externalFieldErrors.slot_duration}
                helperText={externalFieldErrors.slot_duration || ''}
                inputProps={{ min: 5, step: 5 }} sx={{ width: 105 }} />
            </Box>
            <Divider sx={{ my: 2 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {t('bookingSettings.q5.bufferTitle')}
              </Typography>
            </Divider>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField fullWidth type="number" size="small" label={t('bookingSettings.q5.bufferBefore')}
                  value={bookingSettings.buffer_before}
                  onChange={e => setBookingSettings(p => ({ ...(p || {}), buffer_before: parseInt(e.target.value) || 0 }))}
                  helperText={t('bookingSettings.q5.bufferBeforeHelper')}
                  inputProps={{ min: 0, step: 5 }} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth type="number" size="small" label={t('bookingSettings.q5.bufferAfter')}
                  value={bookingSettings.buffer_after}
                  onChange={e => setBookingSettings(p => ({ ...(p || {}), buffer_after: parseInt(e.target.value) || 0 }))}
                  helperText={t('bookingSettings.q5.bufferAfterHelper')}
                  inputProps={{ min: 0, step: 5 }} />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}

      {/* ══════════════════════════════════════════════════════════════
          Q6 — Horaires d'ouverture (si slot-based, optionnel)
      ══════════════════════════════════════════════════════════════ */}
      {isSlotBased && (
        <Accordion sx={subSx(colors.q6)}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={sumSx(colors.q6)}>
            <Box display="flex" alignItems="center" gap={1}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: colors.q6 }} />
              <Typography fontWeight={700} sx={{ color: colors.q6 }}>{t('bookingSettings.q6.sectionTitle')}</Typography>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); setHelpModalOpen('q6'); }}
                sx={{ ml: 0.5, color: colors.q6, '&:hover': { background: alpha(colors.q6, 0.15) } }}
              >
                <HelpIcon sx={{ fontSize: 18 }} />
              </IconButton>
              {openingHours.length > 0
                ? <Chip size="small" label={`${openingHours.length} jour(s)`}
                    sx={{
                      ml: 1,
                      backgroundColor: colors.q6,
                      color: theme.palette.getContrastText(colors.q6),
                      fontWeight: 600,
                      height: 22,
                      fontSize: '0.7rem',
                    }} />
                : <Chip size="small" label={t('bookingSettings.common.optional')} variant="outlined"
                    sx={{ ml: 1, borderColor: colors.q6, color: colors.q6, height: 22, fontSize: '0.7rem' }} />
              }
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 2, pb: 3, px: 3 }}>
            <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
              <QNum n={isShared ? '7' : '6'} />
              <Typography variant="subtitle1" fontWeight={600}>
                {t('bookingSettings.q6.bodyTitle')}
              </Typography>
            </Box>
            <Alert severity="info" sx={{ mb: 2, fontSize: '0.82rem' }}>
              {t('bookingSettings.q6.alertAllDay')}
            </Alert>
            {!!externalFieldErrors.opening_hours && (
              <Alert severity="error" sx={{ mb: 2, fontSize: '0.82rem' }}>
                {externalFieldErrors.opening_hours}
              </Alert>
            )}
            <Grid container spacing={1.5}>
              {WEEK_DAYS.map(({ day, label }) => {
                const ex = openingHours.find(h => h.day === day);
                return (
                  <Grid item xs={12} sm={6} md={4} key={`oh-${day}`}>
                    <Box sx={{
                      border: `2px solid ${ex ? colors.q6 : theme.palette.divider}`,
                      borderRadius: 2, p: 1.5,
                      backgroundColor: ex ? alpha(colors.q6, 0.10) : theme.palette.background.default,
                      transition: 'all 0.15s',
                    }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={ex ? 1.5 : 0}>
                        <Typography variant="body2" fontWeight={600} sx={{ color: ex ? colors.q6 : theme.palette.text.secondary }}>
                          {label}
                        </Typography>
                        {/* Toggle switch maison */}
                        <Box
                          onClick={() => {
                            setBookingSettings(p => {
                              const prev = p || {};
                              const prevOpeningHours = Array.isArray(prev.opening_hours) ? prev.opening_hours : [];

                              if (ex) {
                                return {
                                  ...prev,
                                  opening_hours: prevOpeningHours.filter(h => h.day !== day),
                                };
                              }

                              return {
                                ...prev,
                                opening_hours: [...prevOpeningHours, { day, from: '09:00', to: '18:00' }],
                              };
                            });
                          }}
                          sx={{
                            width: 36, height: 20, borderRadius: 10, cursor: 'pointer',
                            backgroundColor: ex ? colors.q6 : theme.palette.action.disabled,
                            position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                          }}>
                          <Box sx={{
                            width: 16, height: 16, borderRadius: '50%', backgroundColor: theme.palette.common.white,
                            position: 'absolute', top: 2, left: ex ? 18 : 2,
                            transition: 'left 0.2s', boxShadow: theme.shadows[1],
                          }} />
                        </Box>
                      </Box>
                      {ex && (
                        <Box display="flex" gap={1}>
                          <TextField type="time" size="small" label={t('bookingSettings.q6.from')} value={ex.from}
                            onChange={e => setBookingSettings(p => {
                              const prev = p || {};
                              const prevOpeningHours = Array.isArray(prev.opening_hours) ? prev.opening_hours : [];
                              return {
                                ...prev,
                                opening_hours: prevOpeningHours.map(h => h.day === day ? { ...h, from: e.target.value } : h),
                              };
                            })}
                            error={!!(toMinutes(ex.from) !== null && toMinutes(ex.to) !== null && toMinutes(ex.to) <= toMinutes(ex.from))}
                            sx={{ flex: 1 }} />
                          <TextField type="time" size="small" label={t('bookingSettings.q6.to')} value={ex.to}
                            onChange={e => setBookingSettings(p => {
                              const prev = p || {};
                              const prevOpeningHours = Array.isArray(prev.opening_hours) ? prev.opening_hours : [];
                              return {
                                ...prev,
                                opening_hours: prevOpeningHours.map(h => h.day === day ? { ...h, to: e.target.value } : h),
                              };
                            })}
                            error={!!(toMinutes(ex.from) !== null && toMinutes(ex.to) !== null && toMinutes(ex.to) <= toMinutes(ex.from))}
                            helperText={
                              (toMinutes(ex.from) !== null && toMinutes(ex.to) !== null && toMinutes(ex.to) <= toMinutes(ex.from))
                                ? t('bookingSettings.q6.endAfterStart')
                                : ''
                            }
                            sx={{ flex: 1 }} />
                        </Box>
                      )}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}

      {coherenceAlerts.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {coherenceAlerts.map((issue, idx) => (
            <Alert key={`coh-${idx}`} severity={issue.severity} sx={{ fontSize: '0.86rem' }}>
              {issue.message}
            </Alert>
          ))}
        </Box>
      )}

      {externalGlobalErrors.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {externalGlobalErrors.map((msg, idx) => (
            <Alert key={`ext-coh-${idx}`} severity="error" sx={{ fontSize: '0.86rem' }}>
              {msg}
            </Alert>
          ))}
        </Box>
      )}

      {/* ══════════════════════════════════════════════════════════════
          Récapitulatif
      ══════════════════════════════════════════════════════════════ */}
      <Box sx={{
        mt: 2, p: 2, borderRadius: 3,
        background: `linear-gradient(135deg,${alpha(colors.q1, 0.10)},${alpha(colors.q5, 0.10)})`,
        border: `1px solid ${alpha(colors.q5, 0.35)}`,
      }}>
        <Typography variant="body2" fontWeight={700} sx={{ color: colors.q1 }} mb={1}>
          {t('bookingSettings.recap.title')}
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Chip size="small"
            icon={<EventAvailableIcon sx={{ fontSize: 14 }} />}
            label={
              bookingMode === 'date_range'
                ? (bookingSettings?.date_range_mode === 'nights' ? t('bookingSettings.recap.chipNights') : t('bookingSettings.recap.chipDays'))
                : bookingMode === 'time_slot' ? t('bookingSettings.recap.chipTimeSlot')
                : bookingMode === 'time_slot_capacity' ? t('bookingSettings.recap.chipSharedSlot')
                : '—'
            }
            sx={{ backgroundColor: alpha(colors.q1, 0.15), fontWeight: 600 }} />
          <Chip size="small" label={t('bookingSettings.recap.chipUnits', { count: stockQuantity })}
            sx={{ backgroundColor: alpha(colors.q2, 0.15), fontWeight: 600 }} />
          {isShared && formData.capacity_default &&
            <Chip size="small" label={t('bookingSettings.recap.chipPlaces', { count: formData.capacity_default })}
              sx={{ backgroundColor: alpha(colors.q3, 0.15), fontWeight: 600 }} />}
          {bookingSettings.unit &&
            <Chip size="small"
              label={bookingSettings.unit === 'day' ? t('bookingSettings.recap.chipUnit.day') : bookingSettings.unit === 'hour' ? t('bookingSettings.recap.chipUnit.hour') : t('bookingSettings.recap.chipUnit.session')}
              sx={{ backgroundColor: alpha(colors.q4, 0.15), fontWeight: 600 }} />}
          {bookingSettings.min_duration &&
            <Chip size="small" label={t('bookingSettings.recap.chipMin', { count: bookingSettings.min_duration })}
              sx={{ backgroundColor: alpha(colors.q4, 0.15), fontWeight: 600 }} />}
          {bookingSettings.max_duration &&
            <Chip size="small" label={t('bookingSettings.recap.chipMax', { count: bookingSettings.max_duration })}
              sx={{ backgroundColor: alpha(colors.q4, 0.15), fontWeight: 600 }} />}
          {isSlotBased && bookingSettings.slot_duration &&
            <Chip size="small" label={t('bookingSettings.recap.chipSlotDuration', { count: bookingSettings.slot_duration })}
              sx={{ backgroundColor: alpha(colors.q5, 0.15), fontWeight: 600 }} />}
          {isSlotBased && openingHours.length > 0 &&
            <Chip size="small" label={t('bookingSettings.recap.chipOpenDays', { count: openingHours.length })}
              sx={{ backgroundColor: alpha(colors.q6, 0.15), fontWeight: 600 }} />}
        </Box>
      </Box>

      {/* ══════════════════════════════════════════════════════════════
          Modals d'aide pour chaque question
      ══════════════════════════════════════════════════════════════ */}
      {['q1', 'q2', 'q3', 'q4', 'q5', 'q6'].map((qKey) => (
        <Dialog
          key={`modal-${qKey}`}
          open={helpModalOpen === qKey}
          onClose={() => setHelpModalOpen(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle
            sx={{
              background: `linear-gradient(135deg, ${colors[qKey]} 30%, ${alpha(colors[qKey], 0.7)} 90%)`,
              color: theme.palette.getContrastText(colors[qKey]),
              fontWeight: 700,
              fontSize: '1.1rem',
            }}
          >
            {modalContent[qKey]?.title}
          </DialogTitle>
          <DialogContent sx={{ pt: 3, pb: 2 }}>
            <Box display="flex" flexDirection="column" gap={2.5}>
              {modalContent[qKey]?.examples.map((example, idx) => (
                <Box key={idx}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        backgroundColor: alpha(colors[qKey], 0.15),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        color: colors[qKey],
                      }}
                    >
                      {idx + 1}
                    </Box>
                    <Typography variant="body2" fontWeight={700} sx={{ color: colors[qKey] }}>
                      {example.title}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ ml: 4.5, lineHeight: 1.6 }}
                  >
                    {example.desc}
                  </Typography>
                </Box>
              ))}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 1 }}>
            <Button
              onClick={() => setHelpModalOpen(null)}
              variant="contained"
              sx={{ backgroundColor: colors[qKey] }}
            >
              {t('bookingSettings.modal.understood')}
            </Button>
          </DialogActions>
        </Dialog>
      ))}
    </React.Fragment>
  );
}
